import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fmtNum, fmtHM } from '../../lib/formatHelpers';

/**
 * V2 EtatBlock — Bloc "état de l'équipe" en 2 onglets :
 *  - Today : breakdown par serveur + top 3 créateurs du jour
 *  - Month : breakdown par serveur + progression MTD
 *
 * NoiRock adaptation Phase 1 :
 *  - Pas de financial $$. On affiche sessions / hours / valid_go_live_days
 *    cumulés par serveur.
 *  - Pas de "projection vs objectif" (les objectifs sont en colonnes _enc).
 *
 * Props :
 *  - byServer : { [server]: { sessions, hours, validDays, active, total } }
 *  - topToday : array<{ creator_id, display_name, server, sessions, hours }> (top 3)
 *  - loading
 */
const TabButton = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-3 py-2 text-xs font-semibold uppercase tracking-widest border-b-2 transition-colors ${
      active
        ? 'border-primary text-primary'
        : 'border-transparent text-muted-foreground hover:text-foreground'
    }`}
  >
    {children}
  </button>
);

const ServerRow = ({ name, sessions, hours, validDays, active, total }) => (
  <div className="grid grid-cols-5 gap-3 items-center px-4 py-2.5 text-sm">
    <div className="font-medium text-foreground truncate">{name}</div>
    <div className="text-right tabular-nums text-muted-foreground">{fmtNum(active)} / {fmtNum(total)}</div>
    <div className="text-right tabular-nums">{fmtNum(sessions)}</div>
    <div className="text-right tabular-nums">{fmtHM((hours || 0) * 3600)}</div>
    <div className="text-right tabular-nums">{fmtNum(validDays)}</div>
  </div>
);

const EtatBlock = ({ byServer = {}, topToday = [], loading = false }) => {
  const { t } = useTranslation();
  const [tab, setTab] = useState('today');

  const sortedServers = useMemo(
    () => Object.entries(byServer).sort((a, b) => (b[1].sessions || 0) - (a[1].sessions || 0)),
    [byServer]
  );

  return (
    <section className="rounded-2xl bg-card border border-border overflow-hidden">
      <div className="border-b border-border bg-surface-2 flex items-center justify-between px-2">
        <div className="flex">
          <TabButton active={tab === 'today'} onClick={() => setTab('today')}>
            {t('managerV2.etatBlock.tabs.today', 'Today')}
          </TabButton>
          <TabButton active={tab === 'month'} onClick={() => setTab('month')}>
            {t('managerV2.etatBlock.tabs.month', 'Month')}
          </TabButton>
        </div>
      </div>

      {tab === 'today' && (
        <div className="px-5 py-4">
          {loading ? (
            <div className="h-32 skeleton rounded" />
          ) : topToday.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">
              {t('managerV2.etatBlock.noToday', 'No activity today yet.')}
            </p>
          ) : (
            <ol className="space-y-2">
              {topToday.map((c, i) => (
                <li key={c.creator_id} className="flex items-center gap-3 py-1.5">
                  <span className={`flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold ${
                    i === 0 ? 'bg-warning/20 text-warning' : i === 1 ? 'bg-muted text-foreground' : 'bg-muted/60 text-muted-foreground'
                  }`}>
                    {i + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-foreground truncate">{c.display_name}</div>
                    <div className="text-xs text-muted-foreground">{c.server || '—'}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-semibold tabular-nums">{fmtNum(c.sessions)} sessions</div>
                    <div className="text-xs text-muted-foreground tabular-nums">{fmtHM((c.hours || 0) * 3600)}</div>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>
      )}

      {tab === 'month' && (
        <div>
          <div className="grid grid-cols-5 gap-3 px-4 py-2 border-b border-border bg-surface-2 text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">
            <div>{t('managerV2.etatBlock.server', 'Server')}</div>
            <div className="text-right">{t('managerV2.etatBlock.active', 'Active')}</div>
            <div className="text-right">{t('managerV2.etatBlock.sessions', 'Sessions')}</div>
            <div className="text-right">{t('managerV2.etatBlock.hours', 'Hours')}</div>
            <div className="text-right">{t('managerV2.etatBlock.validDays', 'Valid days')}</div>
          </div>
          {loading ? (
            <div className="h-32 skeleton" />
          ) : sortedServers.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">
              {t('managerV2.etatBlock.noServers', 'No data yet.')}
            </p>
          ) : (
            <div className="divide-y divide-border">
              {sortedServers.map(([name, data]) => (
                <ServerRow key={name} name={name} {...data} />
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default EtatBlock;
